import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { Building2, CreditCard } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, DateInput, EmptyState, Input, Select, SearchableDropdown, Toggle } from '@/components/ui'
import { PaymentFields } from '@/components/payments/PaymentFields'
import { PaymentMethodEnableNote } from '@/components/payments/PaymentMethodEnableNote'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday } from '@/lib/date'
import { buildPaymentMethodOptions } from '@/lib/paymentMethodOptions'
import { buildInvoiceOptionLabel } from '@/lib/invoiceOptionLabel'
import { useProperties } from '@/hooks/useProperties'
import { useCustomer, useCustomerSearch } from '@/hooks/useCustomers'
import { useCreateStandalonePayment } from '@/hooks/usePayments'
import { useInvoices } from '@/hooks/useInvoices'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import type { Customer, Invoice } from '@/types'

// T149-088 (PAY-3 decision A): the invoice picker's own sentinel for "pick no
// invoice, use the fields below instead," deliberately not '' (SearchableDropdown
// treats an empty value as "nothing chosen yet" and shows its placeholder, not
// this option's own label).
const NO_INVOICE_VALUE = 'none'

// ---------------------------------------------------------------------------
// Form shape
// ---------------------------------------------------------------------------

interface QuickPayFormData {
  amount: number
  payment_method: string
  paid_at: string
  reference: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * P005 / T149-088: QuickPayFormPage, the ONE record-payment entry point
 * (/payments/record). Merges what used to be two separate pages/routes:
 *   - the invoice-attached create form (formerly PaymentFormPage's create
 *     branch, reached at /payments/new)
 *   - the standalone/invoice-optional quick-pay fields (this page, before
 *     T149-088)
 *
 * An invoice picker sits at the top; picking a real invoice renders
 * PaymentFields against it (same as PaymentFormPage's create branch did).
 * Picking "ไม่มีใบแจ้งหนี้" (the default) shows the standalone quick-pay
 * fields below, including the unknown-payer toggle (T149-073). Any role
 * that can reach this page can use either branch (PaymentPolicy#create? is
 * same_account? only, so there was never a server-side role restriction on
 * the invoice-attached path, only a frontend-only admin/owner gate that
 * this merge removes).
 *
 * Prefill via query params:
 *   ?customer_id=123  : pre-selects the quick-pay customer AND narrows the
 *                        invoice picker to that customer's own invoices
 *   ?lease_id=456     : pre-selects by lease (customer resolved on form load)
 *   ?unit_id=789      : informational only (unit context; customer still required)
 *   ?amount=          : prefills the amount (quick-pay field, or PaymentFields
 *                        once an invoice is picked)
 *   ?paid_at=         : prefills the date, same as ?amount=
 *   ?return_to=       : an in-app path to navigate to instead of /payments/:id
 *                        after an invoice-attached save (bank reconciliation's
 *                        return-to-row behavior)
 *
 * Condo accounts: the standalone/no-invoice branch is hidden since
 * OwnerContract has rent=0 and there is no meaningful standalone rent to
 * record, but the invoice picker (e.g. for a CAF invoice) stays available.
 */
export function QuickPayFormPage() {
  const { t, i18n } = useTranslation('payments')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('payments')

  const { data: accountSettings } = useAccountSettings()
  const isCondominium = accountSettings?.account_type === 'condominium'
  const { format, timezone, calendar } = useLocalizationConfig()

  // Query params for pre-fill
  const prefillCustomerId = searchParams.get('customer_id')
  const prefillLeaseId = searchParams.get('lease_id')
  const prefillPropertyId = searchParams.get('property_id')
  // T149-088: carried over from PaymentFormPage's create branch (bank
  // reconciliation's "Record payment" deep-link) for the invoice-attached path.
  const amountParam = searchParams.get('amount')
  const prefillAmount = amountParam ? Number(amountParam) : undefined
  const prefillPaidAt = searchParams.get('paid_at') || undefined
  const returnTo = searchParams.get('return_to')

  // Customer search (EP-116 T007: paged + load-more, mirrors ParcelFormPage's
  // search/page/accumulate shape).
  const prefillCustomerIdNum = prefillCustomerId ? Number(prefillCustomerId) : undefined
  const [customerQuery, setCustomerQuery] = useState('')
  const [customerPage, setCustomerPage] = useState(1)
  const [accumulatedCustomers, setAccumulatedCustomers] = useState<Customer[]>([])
  const [selectedCustomerId, setSelectedCustomerId] = useState(prefillCustomerId ?? '')
  // T149-073 (MON-19 A): on hides the customer picker and skips the
  // customer_required guard; the payment posts with unknown_payer: true and
  // no customer_id (T149-070 on the server).
  const [unknownPayer, setUnknownPayer] = useState(false)
  // Browse-on-focus (S004): SearchableDropdown fires onSearch('') on open, so
  // fetchOnEmpty lets that blank query list recent customers instead of a
  // dead "no results" state.
  const { data: customerSearchData, isFetching: isCustomerLoading } = useCustomerSearch(customerQuery, {
    fetchOnEmpty: true,
    page: customerPage,
  })

  // Reset the accumulated list when the search query changes, a new query
  // must not append onto a stale previous query's results.
  useEffect(() => {
    setCustomerPage(1)
    setAccumulatedCustomers([])
  }, [customerQuery])

  // Append new page results onto the accumulated list; replace on page 1.
  useEffect(() => {
    if (!customerSearchData?.data) return
    setAccumulatedCustomers((prev) => {
      if (customerPage === 1) return customerSearchData.data
      const existingIds = new Set(prev.map((c) => c.id))
      return [...prev, ...customerSearchData.data.filter((c) => !existingIds.has(c.id))]
    })
  }, [customerSearchData, customerPage])

  // Saved-value guard: this page has no edit mode (it only ever creates a new
  // standalone payment), so the "preserve the saved selection when editing"
  // criterion doesn't literally apply here. But ?customer_id= prefills a
  // customer that can sit outside the loaded/searched page (or the
  // recent-first default listing) just the same, without a direct fetch the
  // picker would silently fall back to its placeholder even though a real
  // customer_id is already selected. Same fallback-fetch shape as LeaseFields'
  // defaultCustomerId guard.
  const needFallbackCustomer =
    !!prefillCustomerIdNum && !accumulatedCustomers.some((c) => c.id === prefillCustomerIdNum)
  const { data: fallbackCustomer } = useCustomer(
    needFallbackCustomer && prefillCustomerIdNum ? prefillCustomerIdNum : 0,
  )

  const customerOptions = (() => {
    const opts = accumulatedCustomers.map((c) => ({
      value: String(c.id),
      label: c.name + (c.phone ? ` · ${c.phone}` : ''),
      // S005: surface the current unit/room number as a chip so an operator
      // can disambiguate same-name customers by which room they're in.
      chip: c.unit_number ?? undefined,
    }))
    if (needFallbackCustomer && fallbackCustomer && !opts.some((o) => o.value === String(fallbackCustomer.id))) {
      opts.push({
        value: String(fallbackCustomer.id),
        label: fallbackCustomer.name + (fallbackCustomer.phone ? ` · ${fallbackCustomer.phone}` : ''),
        chip: fallbackCustomer.unit_number ?? undefined,
      })
    }
    return opts
  })()
  const hasMoreCustomers = !!customerSearchData?.meta && customerSearchData.meta.current_page < customerSearchData.meta.total_pages

  // T149-088: invoice picker, moved over from PaymentFormPage's create
  // branch (EP-116 T009: paged + load-more, same search/page/accumulate
  // shape as the customer picker above). ?customer_id= (if present) narrows
  // this to that customer's own unpaid invoices, same as it pre-selects the
  // quick-pay customer below.
  const [invoiceQuery, setInvoiceQuery] = useState('')
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(NO_INVOICE_VALUE)
  const [invoicePage, setInvoicePage] = useState(1)
  const [accumulatedInvoices, setAccumulatedInvoices] = useState<Invoice[]>([])
  // EP-97: allPeriods so the picker can settle an unpaid invoice from ANY
  // period, not just the latest billing period the list endpoint defaults to.
  const { data: invoicesData, isFetching: invoicesLoading } = useInvoices(
    invoicePage,
    'unpaid',
    undefined,
    undefined,
    undefined,
    prefillCustomerIdNum,
    invoiceQuery || undefined,
    undefined,
    { allPeriods: true },
  )

  useEffect(() => {
    setInvoicePage(1)
    setAccumulatedInvoices([])
  }, [invoiceQuery])

  useEffect(() => {
    if (!invoicesData?.data) return
    setAccumulatedInvoices((prev) => {
      if (invoicePage === 1) return invoicesData.data
      const existingIds = new Set(prev.map((inv) => inv.id))
      return [...prev, ...invoicesData.data.filter((inv) => !existingIds.has(inv.id))]
    })
  }, [invoicesData, invoicePage])
  const hasMoreInvoices =
    !!invoicesData?.meta && invoicesData.meta.current_page < invoicesData.meta.total_pages

  const invoiceOptions = [
    { value: NO_INVOICE_VALUE, label: t('form.no_invoice_option') },
    ...accumulatedInvoices.map((inv) => ({ value: String(inv.id), label: buildInvoiceOptionLabel(inv) })),
  ]
  // '' (nothing chosen) is treated the same as the default NO_INVOICE_VALUE:
  // both mean "use the fields below," they just differ in whether the
  // picker's own box shows the ไม่มีใบแจ้งหนี้ label or its placeholder.
  const selectedInvoiceIdNum =
    selectedInvoiceId && selectedInvoiceId !== NO_INVOICE_VALUE ? Number(selectedInvoiceId) : null

  // P008/F2: standalone payments must own a property. Load the account's
  // properties for the picker; if none exist we send the operator to create one.
  const { data: propertiesData } = useProperties()
  // Wrap the `?? ||` logical expression in its own useMemo so the reference
  // is stable across renders (used as a useEffect dep below).
  const properties = useMemo(() => propertiesData?.data ?? [], [propertiesData])
  // `propertiesData` is only truthy once the query resolves, gate the no-property
  // state on that so we don't flash the empty CTA while loading.
  const propertiesLoaded = !!propertiesData
  const [selectedPropertyId, setSelectedPropertyId] = useState(prefillPropertyId ?? '')
  const propertyOptions = [
    { value: '', label: t('quick_pay.property_placeholder') },
    ...properties.map((p) => ({ value: String(p.id), label: p.name })),
  ]
  // Auto-select the sole property once the list resolves (the operator can't
  // choose anything else anyway). Only when nothing is selected yet.
  useEffect(() => {
    if (!selectedPropertyId && properties.length === 1) {
      setSelectedPropertyId(String(properties[0].id))
    }
  }, [properties, selectedPropertyId])

  // Payment method options from account settings
  const paymentMethodOptions = buildPaymentMethodOptions(
    accountSettings?.settings?.payment_methods,
    (key) => t(`methods.${key}`),
  )

  const today = localToday(timezone)

  const createStandalonePayment = useCreateStandalonePayment()

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<QuickPayFormData>({
    defaultValues: {
      amount: prefillAmount ?? 0,
      payment_method: paymentMethodOptions[0]?.value ?? 'cash',
      paid_at: prefillPaidAt ?? today,
      reference: '',
    },
  })

  // PAY-4 decision C: a non-blocking future-date warning. There is no
  // invoice on a standalone payment, so only the future-date half of the
  // check applies here (PaymentFields.tsx also warns before an invoice's
  // billed_at, which has no equivalent on this page).
  const watchedPaidAt = watch('paid_at')
  const paidAtInFuture = !!(watchedPaidAt && watchedPaidAt > today)

  const onSubmit = async (data: QuickPayFormData) => {
    try {
      const payload: Parameters<typeof createStandalonePayment.mutateAsync>[0] = {
        property_id: Number(selectedPropertyId),
        amount: data.amount,
        payment_method: data.payment_method,
        paid_at: data.paid_at,
        reference: data.reference || undefined,
      }
      if (unknownPayer) {
        payload.unknown_payer = true
      } else {
        payload.customer_id = Number(selectedCustomerId)
      }
      // If we have a lease_id from context, also pass it so the BE can derive
      // both customer and lease for Meilisearch indexing.
      if (prefillLeaseId) {
        payload.lease_id = Number(prefillLeaseId)
      }
      await createStandalonePayment.mutateAsync(payload)
      addToast({ type: 'success', message: t('quick_pay.success') })
      navigate('/payments')
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  // T149-073 (18-F12): reproduced at build time. `handleSubmit(onSubmit)`
  // validates every RHF-registered field (amount/payment_method/paid_at)
  // and only calls `onSubmit` once ALL of them pass. With amount still at
  // its default 0 (never edited: 0 renders as a non-empty "0", so RHF's own
  // `required` rule is satisfied and only the separate `min: 0.01` rule
  // trips), `onSubmit` was never reached at all, so the customer_required
  // toast this file already had never fired. The visible symptom read as
  // "leaving Customer blank does nothing," but the real, silent block was
  // an unrelated field's own validation, not a missing toast. These two
  // manual checks now run in the native form submit handler, before RHF's
  // own validation, so a blank customer (or property) is reported
  // regardless of what state Amount happens to be in.
  const submitForm = handleSubmit(onSubmit)
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!unknownPayer && !selectedCustomerId) {
      addToast({ type: 'error', message: t('quick_pay.customer_required') })
      return
    }
    if (!selectedPropertyId) {
      addToast({ type: 'error', message: t('quick_pay.property_required') })
      return
    }
    void submitForm(e)
  }

  // T149-088: the standalone (no-invoice) branch below is unavailable for
  // condominium accounts (OwnerContract has rent_amount=0; there is no
  // meaningful standalone rent to record) and for an account with no
  // properties yet (a standalone payment must be scoped to one, F2). Neither
  // guard applies to the invoice-attached branch above it: a condo account
  // still has real CAF invoices to pay, and picking one needs no property
  // list of its own.
  const noInvoiceSection = isCondominium ? (
    <div>
      <p className="py-8 text-center text-base-content/60">{t('quick_pay.not_available_condo')}</p>
      <div className="flex justify-center mt-4">
        <Button variant="ghost" onClick={() => navigate('/payments')}>{tc('actions.back')}</Button>
      </div>
    </div>
  ) : propertiesLoaded && properties.length === 0 ? (
    <EmptyState
      icon={<Building2 className="w-16 h-16" />}
      title={t('quick_pay.no_property_title')}
      description={t('quick_pay.no_property_hint')}
      action={
        <Link to="/properties/new">
          <Button leftIcon={<Building2 className="w-4 h-4" />}>{t('quick_pay.no_property_cta')}</Button>
        </Link>
      }
    />
  ) : (
    <form onSubmit={handleFormSubmit} className="space-y-4" noValidate>
      {/* Property (F2: required; the payment is scoped to this building) */}
      <Select
        label={t('quick_pay.property_label')}
        options={propertyOptions}
        value={selectedPropertyId}
        onChange={(e) => setSelectedPropertyId(e.target.value)}
      />

      {/* T149-073 (MON-19 A): off (default) keeps today's required
          customer picker; on hides it and the payment posts
          unknown_payer: true with no customer_id. */}
      <Toggle
        label={t('quick_pay.unknown_payer_label')}
        checked={unknownPayer}
        onChange={(e) => setUnknownPayer(e.target.checked)}
      />

      {/* Customer picker */}
      {!unknownPayer && (
        <SearchableDropdown
          label={t('quick_pay.customer_label')}
          placeholder={t('quick_pay.customer_placeholder')}
          value={selectedCustomerId}
          options={customerOptions}
          onChange={setSelectedCustomerId}
          onSearch={setCustomerQuery}
          isLoading={isCustomerLoading}
          onLoadMore={() => setCustomerPage((p) => p + 1)}
          hasMore={hasMoreCustomers}
        />
      )}

      {/* Amount */}
      <Input
        label={t('quick_pay.amount_label', { currency: tc('currency.thb') })}
        type="number"
        step="0.01"
        error={errors.amount?.message}
        {...register('amount', {
          required: tc('errors.required'),
          valueAsNumber: true,
          min: { value: 0.01, message: tc('errors.positive_number') },
        })}
      />

      {/* Payment method */}
      <Select
        label={t('labels.method')}
        options={paymentMethodOptions}
        error={errors.payment_method?.message}
        {...register('payment_method', { required: tc('errors.required') })}
      />

      {/* PAY-2 (T149-086): always visible, regardless of how many methods
          are already enabled, same note and keys as PaymentFields. */}
      <PaymentMethodEnableNote />

      {/* Date */}
      <div>
        <DateInput
          label={t('labels.date')}
          error={errors.paid_at?.message}
          defaultValue={prefillPaidAt ?? today}
          dateFormat={format}
          timezone={timezone}
          calendar={calendar}
          locale={i18n.language}
          {...register('paid_at', { required: tc('errors.required') })}
        />
        {paidAtInFuture && (
          <p className="mt-1 text-xs text-warning" role="note">
            {t('quick_pay.date_future_warning')}
          </p>
        )}
      </div>

      {/* Reference (optional) */}
      <Input
        label={t('labels.reference')}
        placeholder={t('quick_pay.reference_placeholder')}
        {...register('reference')}
      />

      <div className="flex justify-end gap-3 pt-2">
        <Button variant="ghost" type="button" onClick={() => navigate('/payments')}>
          {tc('actions.cancel')}
        </Button>
        <Button
          type="submit"
          loading={isSubmitting || createStandalonePayment.isPending}
          leftIcon={<CreditCard className="w-4 h-4" />}
        >
          {t('quick_pay.submit')}
        </Button>
      </div>
    </form>
  )

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: t('title'), href: '/payments' }, { label: t('quick_pay.title') }]} />
      <PageHeader
        title={t('quick_pay.title')}
        description={t('quick_pay.description')}
      />

      <Card>
        <div className="space-y-4">
          {/* T149-088 (PAY-3 decision A/B): the invoice picker. Picking a
              real invoice renders PaymentFields against it, same as
              PaymentFormPage's former create branch; the default
              ไม่มีใบแจ้งหนี้ choice shows the standalone fields below. */}
          <SearchableDropdown
            label={t('form.select_invoice')}
            placeholder={t('form.invoice_search_placeholder')}
            value={selectedInvoiceId}
            options={invoiceOptions}
            onChange={setSelectedInvoiceId}
            onSearch={setInvoiceQuery}
            isLoading={invoicesLoading}
            onLoadMore={() => setInvoicePage((p) => p + 1)}
            hasMore={hasMoreInvoices}
          />

          {selectedInvoiceIdNum !== null ? (
            <PaymentFields
              payment={null}
              invoiceId={selectedInvoiceIdNum}
              prefillAmount={prefillAmount}
              prefillPaidAt={prefillPaidAt}
              onSave={(p) => navigate(returnTo || `/payments/${p.id}`)}
            />
          ) : (
            noInvoiceSection
          )}
        </div>
      </Card>
    </div>
  )
}
